import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { tickToPrice } from '@uniswap/v3-sdk'
import { usePostHog } from 'posthog-js/react'
import { erc20Abi } from 'viem'
import {
  useAccount,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'

import { DexPositionManagerAbi } from '../blockchain/abis/DexPositionManager'
import { FissionVaultAbi } from '../blockchain/abis/FissionVault'
import { useCreateAndSignPermit2 } from '../blockchain/useCreateAndSignPermit2'
import { useGetContractAddress } from '../crypto_contracts'
import {
  amount_to_tokens,
  encoded_to_percentage,
  tokens_to_amount,
} from '../helpers/block_helpers'
import { token_balance_change } from '../helpers/invalidator_helpers'
import { convert_to_u_token } from '../helpers/tick_helpers'
import { disableKeyWhenMissing, reportEnableFailed } from '../helpers/utilities'
import { useGetSymbolToDecimal } from '../portfolio'
import {
  useGetTokenAddress,
  type VaultSymbol,
  vaultToFundSymbol,
} from '../products'
import { keys as token_auth_keys } from '../token_auth'

import { keys, useVaultInfo } from './vaults_queries'

export type VaultBalances = {
  usdc_balance: bigint
  fund_balance: bigint
  usdc_auto_position_amount: bigint
  fund_auto_position_amount: bigint
  usdc_manual_position_amount: bigint
  fund_manual_position_amount: bigint
}

export function balances_is_empty(balances: VaultBalances): boolean {
  return (
    balances.usdc_balance == 0n &&
    balances.fund_balance == 0n &&
    balances.usdc_auto_position_amount == 0n &&
    balances.fund_auto_position_amount == 0n &&
    balances.usdc_manual_position_amount == 0n &&
    balances.fund_manual_position_amount == 0n
  )
}

export function useGetVaultBalances({ symbol }: { symbol?: VaultSymbol }) {
  const vault_address = useVaultInfo({ symbol }).data?.vault_contract_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = symbol != null && client != null && vault_address != null

  reportEnableFailed({
    name: 'useGetVaultBalances',
    parts: {
      symbol,
      client,
      vault_address,
    },
  })

  return useQuery<VaultBalances>({
    queryKey: keys.vaults.details({ symbol: disableKeyWhenMissing(symbol) })
      ._ctx.balances.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await client.readContract({
            abi: FissionVaultAbi,
            functionName: 'vaultBalances',
            address: vault_address,
          })

          return {
            usdc_balance: result.undeployedWalletBalance.usdcTokensAmount,
            fund_balance: result.undeployedWalletBalance.fundTokensAmount,
            usdc_auto_position_amount:
              result.autoPositionBalance.usdcTokensAmount,
            fund_auto_position_amount:
              result.autoPositionBalance.fundTokensAmount,
            usdc_manual_position_amount:
              result.manualPositionBalance.usdcTokensAmount,
            fund_manual_position_amount:
              result.manualPositionBalance.fundTokensAmount,
          }
        },
  })
}

export function useGetVaultTotalTokens({ symbol }: { symbol?: VaultSymbol }) {
  const { data } = useVaultInfo({ symbol })
  const token_address = data?.token_address
  const symbol_to_decimal = useGetSymbolToDecimal()

  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    symbol != null &&
    client != null &&
    token_address != null &&
    symbol_to_decimal.isSuccess

  reportEnableFailed({
    name: 'useGetVaultTotalTokens',
    parts: {
      symbol,
      client,
      token_address,
      symbol_to_decimal,
    },
  })

  return useQuery<number>({
    queryKey: keys.vaults.details({ symbol: disableKeyWhenMissing(symbol) })
      ._ctx.total_tokens.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const shares = await client.readContract({
            abi: erc20Abi,
            functionName: 'totalSupply',
            address: token_address,
          })
          return amount_to_tokens(shares, symbol_to_decimal[symbol])
        },
  })
}

// eslint-disable-next-line complexity
export function useGetVaultAutoRange({ symbol }: { symbol: VaultSymbol }) {
  const fund_symbol = vaultToFundSymbol(symbol)
  const { data } = useVaultInfo({ symbol })
  const token_address = data?.token_address
  const vault_contract_address = data?.vault_contract_address
  const fund_address = useGetTokenAddress({ symbol: fund_symbol })
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const dex_position_manager_address = useGetContractAddress(
    'DEX_POSITION_MANAGER',
  )

  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    symbol != null &&
    client != null &&
    token_address != null &&
    vault_contract_address != null &&
    dex_position_manager_address != null &&
    fund_address != null &&
    usdc_address != null &&
    symbol_to_decimal.isSuccess

  reportEnableFailed({
    name: 'useGetVaultTotalTokens',
    parts: {
      symbol,
      client,
      token_address,
      vault_contract_address,
      dex_position_manager_address,
      fund_address,
      usdc_address,
      symbol_to_decimal,
    },
  })

  return useQuery<{
    price_low: number
    price_high: number
    tick_low: number
    tick_high: number
  }>({
    queryKey: keys.vaults.details({ symbol })._ctx.vault_auto_range.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const fund_token = convert_to_u_token({
            symbol: fund_symbol,
            address: fund_address,
            decimals: symbol_to_decimal[fund_symbol],
          })

          const usdc_token = convert_to_u_token({
            symbol: 'USDC',
            address: usdc_address,
            decimals: symbol_to_decimal['USDC'],
          })

          const auto_id = await client.readContract({
            abi: FissionVaultAbi,
            functionName: 'autoPositionTokenId',
            address: vault_contract_address,
          })

          const position = await client.readContract({
            abi: DexPositionManagerAbi,
            functionName: 'positions',
            address: dex_position_manager_address,
            args: [auto_id],
          })

          const tick_low = position[5]
          const price_low = Number(
            tickToPrice(fund_token, usdc_token, tick_low).toFixed(2),
          )
          const tick_high = position[6]
          const price_high = Number(
            tickToPrice(fund_token, usdc_token, tick_high).toFixed(2),
          )

          return { price_low, price_high, tick_low, tick_high }
        },
  })
}

export function useGetVaultExchangePricePerShare({
  symbol,
}: {
  symbol?: VaultSymbol
}) {
  const { data } = useVaultInfo({ symbol })
  const vault_contract_address = data?.vault_contract_address

  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    symbol != null && client != null && vault_contract_address != null

  reportEnableFailed({
    name: 'useGetVaultExchangePricePerShare',
    parts: {
      symbol,
      client,
      vault_contract_address,
    },
  })

  return useQuery<number>({
    queryKey: keys.vaults.details({ symbol: disableKeyWhenMissing(symbol) })
      ._ctx.exchange_rate.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const exchange_rate = await client.readContract({
            abi: FissionVaultAbi,
            functionName: 'exchangeRate',
            address: vault_contract_address,
          })
          const percentage = encoded_to_percentage(exchange_rate)

          return percentage / 100 // price per share
        },
  })
}

export function useGetVaultDepositTokens({
  usdc_tokens,
  fund_tokens,
  symbol,
}: {
  usdc_tokens: number
  fund_tokens: number
  symbol: VaultSymbol
}) {
  const { data } = useVaultInfo({ symbol })
  const fund_symbol = vaultToFundSymbol(symbol)
  const symbol_to_decimal = useGetSymbolToDecimal()
  const vault_contract_address = data?.vault_contract_address

  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    client != null &&
    vault_contract_address != null &&
    symbol_to_decimal.isSuccess

  reportEnableFailed({
    name: 'useGetVaultDepositTokens',
    parts: {
      client,
      vault_contract_address,
      symbol_to_decimal,
    },
  })

  return useQuery({
    queryKey: keys.vaults
      .details({ symbol: disableKeyWhenMissing(symbol) })
      ._ctx.deposit_tokens({ usdc_tokens, fund_tokens }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const exchange_rate = await client.readContract({
            abi: FissionVaultAbi,
            functionName: 'vaultDepositRatio',
            address: vault_contract_address,
            args: [
              // fund, usdc
              tokens_to_amount(fund_tokens, symbol_to_decimal[fund_symbol]),
              tokens_to_amount(usdc_tokens, symbol_to_decimal['USDC']),
            ],
          })
          return {
            deposit_fund_amount: exchange_rate[0],
            deposit_usdc_amount: exchange_rate[1],
          }
        },
  })
}

export function useGetVaultDepositLimitLeft({
  symbol,
}: {
  symbol: VaultSymbol
}) {
  const { data } = useVaultInfo({ symbol })
  const vault_contract_address = data?.vault_contract_address
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    vault_contract_address != null &&
    client != null &&
    symbol_to_decimal.isSuccess

  reportEnableFailed({
    name: 'useGetVaultDepositLimitLeft',
    parts: {
      client,
      vault_contract_address,
      symbol_to_decimal,
    },
  })

  return useQuery<number>({
    queryKey: keys.vaults.details({ symbol })._ctx.deposit_limit.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const results = await client.multicall({
            contracts: [
              {
                abi: FissionVaultAbi,
                address: vault_contract_address,
                functionName: 'totalUsdcDeposited',
              },
              {
                abi: FissionVaultAbi,
                address: vault_contract_address,
                functionName: 'usdcDepositCap',
              },
            ],
          })

          const deposited = results[0].result ?? 0n
          const max = results[1].result ?? 0n

          return amount_to_tokens(max - deposited, symbol_to_decimal[symbol])
        },
  })
}

export function useGetVaultPaused({ symbol }: { symbol: VaultSymbol }) {
  const { data } = useVaultInfo({ symbol })
  const vault_contract_address = data?.vault_contract_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = vault_contract_address != null && client != null

  reportEnableFailed({
    name: 'useGetVaultPaused',
    parts: {
      client,
      vault_contract_address,
    },
  })

  return useQuery<boolean>({
    queryKey: keys.vaults.details({ symbol })._ctx.vault_paused.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          return await client.readContract({
            abi: FissionVaultAbi,
            address: vault_contract_address,
            functionName: 'paused',
          })
        },
  })
}

export function useGetVaultPriceSafety({ symbol }: { symbol: VaultSymbol }) {
  const { data } = useVaultInfo({ symbol })
  const vault_contract_address = data?.vault_contract_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = vault_contract_address != null && client != null

  reportEnableFailed({
    name: 'useGetVaultPriceSafety',
    parts: {
      client,
      vault_contract_address,
    },
  })

  return useQuery<{ price_safety_percentage: number }>({
    queryKey: keys.vaults.details({ symbol })._ctx.vault_settings.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const encoded = await client.readContract({
            abi: FissionVaultAbi,
            address: vault_contract_address,
            functionName: 'allowedUniswapPriceDivergence',
          })

          return { price_safety_percentage: encoded_to_percentage(encoded) }
        },
  })
}

// eslint-disable-next-line complexity, max-lines-per-function
export function useBuyVault({ symbol }: { symbol: VaultSymbol }) {
  const queryClient = useQueryClient()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { address: wallet_address } = useAccount()
  const { data } = useVaultInfo({ symbol })
  const vault_contract_address = data?.vault_contract_address
  const fund_symbol = vaultToFundSymbol(symbol)
  const symbol_to_decimal = useGetSymbolToDecimal()

  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const fund_address = useGetTokenAddress({ symbol: fund_symbol })

  const client = usePublicClient({ chainId: active_chain.id })

  const { createDoubleAsync, isReady: permit_is_ready } =
    useCreateAndSignPermit2()
  const posthog = usePostHog()

  const isReady =
    permit_is_ready &&
    wallet_address != null &&
    symbol_to_decimal.isSuccess &&
    usdc_address != null &&
    fund_address != null &&
    vault_contract_address != null &&
    client != null

  reportEnableFailed({
    name: 'useBuyVault',
    parts: {
      permit_is_ready,
      wallet_address,
      symbol_to_decimal,
      usdc_address,
      fund_address,
      vault_contract_address,
      client,
    },
  })

  const result = useMutation({
    mutationFn: async ({
      deposit_usdc_tokens,
      deposit_fund_tokens,
    }: {
      deposit_usdc_tokens: number
      deposit_fund_tokens: number
    }) => {
      if (!isReady) return

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const deposit_usdc_amount = tokens_to_amount(
        deposit_usdc_tokens,
        symbol_to_decimal['USDC'],
      )
      const deposit_fund_amount = tokens_to_amount(
        deposit_fund_tokens,
        symbol_to_decimal[fund_symbol],
      )

      const { permitDouble, signature } = await createDoubleAsync({
        tokens: [
          {
            symbol: fund_symbol,
            address: fund_address,
            amount: deposit_fund_amount,
          },
          {
            symbol: 'USDC',
            address: usdc_address,
            amount: deposit_usdc_amount,
          },
        ],
        destination_address: vault_contract_address,
      })

      const tx = await writeContractAsync({
        abi: FissionVaultAbi,
        address: vault_contract_address,
        functionName: 'depositWithPermit2',
        args: [
          deposit_fund_amount,
          deposit_usdc_amount,
          permitDouble,
          signature,
        ],
        chainId: active_chain.id,
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('vault deposited', {
        vault: symbol,
        deposit_usdc_tokens,
        deposit_fund_tokens,
      })
    },
    onSuccess: () => {
      void token_balance_change({
        queryClient,
        token_address: data!.token_address,
        wallet_address: wallet_address!,
      })

      void queryClient.invalidateQueries({
        queryKey: token_auth_keys.token_auth.sdd_purchase_stats({
          symbol,
          wallet_address: wallet_address!,
        }).queryKey,
      })
    },
  })
  return {
    ...result,
    isReady,
  }
}

export function useRedeemVault({ symbol }: { symbol: VaultSymbol }) {
  const queryClient = useQueryClient()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { data: vault } = useVaultInfo({ symbol })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })
  const { address: wallet_address } = useAccount()
  const posthog = usePostHog()

  const isReady =
    client != null &&
    vault != null &&
    symbol_to_decimal.isSuccess &&
    wallet_address != null

  const result = useMutation({
    mutationFn: async ({ tokens_in }: { tokens_in: number }) => {
      if (!isReady) return

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const tx = await writeContractAsync({
        abi: FissionVaultAbi,
        address: vault.vault_contract_address,
        functionName: 'withdraw',
        chainId: active_chain.id,
        args: [
          tokens_to_amount(tokens_in, symbol_to_decimal[symbol]),
          wallet_address,
        ],
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('vault redeemed', {
        vault: symbol,
        redeemed_vault_tokens: tokens_in,
      })
    },
    onSuccess: () => {
      void token_balance_change({
        queryClient,
        token_address: vault!.token_address,
        wallet_address: wallet_address!,
      })
    },
  })
  return {
    ...result,
    isReady,
  }
}
